/**
 * Care tracks: what a diagnosis actually changes.
 *
 * A heart-failure patient and a hypertension patient do not have the same daily job. The first
 * weighs themselves before breakfast and watches for swelling; the second takes a blood pressure
 * and watches the weekly average. The track is what the app asks for, shows, and coaches on.
 *
 * The track is a DERIVATION, never state: `trackFor(diagnoses)` is read at view time and is never
 * consulted by `applyEvent`. So a doctor correcting a diagnosis changes today's questions and
 * nothing about the past — no migration, no replay drift, no retroactive coins. For the same
 * reason the track never decides what pays: see EARNING_TABLE, which is diagnosis-blind.
 */

import type { CareGoal, Diagnosis, DiagnosisCode, EarnReason, MetricKind, Symptom } from './types.js';

/** A one-tap thing to do. The id is the contract; the host decides what it looks like. */
export type TrackActionId = 'log-bp' | 'log-weight' | 'log-pulse' | 'log-steps' | 'symptoms' | 'coach' | 'call-clinic';

export interface TrackAction {
  id: TrackActionId;
  label: string;
}

export interface CareTrack {
  id: DiagnosisCode;
  label: string;
  /** The one-line job of this track, in the patient's words. */
  focus: string;
  /** Which metrics this patient is asked for and shown, in order. */
  metrics: MetricKind[];
  /** Which symptoms the report screen offers, most decision-relevant first. */
  symptoms: Symptom[];
  quickActions: TrackAction[];
  /** What a doctor enrolling this patient gets pre-filled. */
  defaultGoals: CareGoal[];
  /** Which earning rows are worth showing. Payout is diagnosis-blind; this is only the list. */
  earning: EarnReason[];
  /** A paragraph for the coach prompt: what matters for this patient and what to watch. */
  coachFocus: string;
  /** The daily check-in line. One question, answerable in two taps. */
  checkInPrompt: string;
}

const GOAL_BP: CareGoal = { kind: 'bp', systolic: 130, diastolic: 80 };
const GOAL_ADHERENCE: CareGoal = { kind: 'adherence', percent: 95 };

export const TRACKS: Readonly<Record<DiagnosisCode, CareTrack>> = {
  'heart-failure': {
    id: 'heart-failure',
    label: 'Heart failure',
    focus: 'Fluid and symptom monitoring',
    metrics: ['weight-lbs', 'heart-rate', 'sleep-hours'],
    symptoms: ['swelling', 'orthopnea', 'shortness-of-breath', 'fatigue', 'dizziness', 'chest-tightness', 'heart-racing'],
    quickActions: [
      { id: 'log-weight', label: 'Log weight' },
      { id: 'symptoms', label: 'Symptoms' },
      { id: 'log-bp', label: 'Log BP' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [{ kind: 'daily-weight', daysPerWeek: 7 }, GOAL_BP, GOAL_ADHERENCE],
    earning: ['weight-log', 'log-bp', 'all-meds', 'coach-checkin'],
    coachFocus:
      'Heart failure. The daily weight is the earliest warning this patient has: 3 lb overnight or 5 lb in a week means fluid, and means calling the clinic rather than waiting. Swelling in the ankles and needing more pillows to sleep are the symptoms that matter most. Salt and fluid are the levers they control.',
    checkInPrompt: 'Step on the scale before breakfast and log it. A jump of a few pounds is the earliest thing we can catch.',
  },
  'post-mi': {
    id: 'post-mi',
    label: 'Post-MI recovery',
    focus: 'Recovery and secondary prevention',
    metrics: ['heart-rate', 'exertion-minutes', 'sleep-hours'],
    symptoms: ['chest-tightness', 'shortness-of-breath', 'fatigue', 'dizziness', 'heart-racing'],
    quickActions: [
      { id: 'log-bp', label: 'Log BP' },
      { id: 'log-steps', label: 'Log steps' },
      { id: 'symptoms', label: 'Symptoms' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [GOAL_BP, { kind: 'steps', target: 6000 }, GOAL_ADHERENCE],
    earning: ['log-bp', 'all-meds', 'coach-checkin', 'steps-goal'],
    coachFocus:
      'Recovering from a heart attack. What matters is steady, gradually increasing activity and never missing the secondary-prevention medications. Chest discomfort that comes on with less exertion than last week is the thing to escalate. Cardiac rehab, if they have been referred, is the single highest-value habit.',
    checkInPrompt: 'How did yesterday feel compared with last week: easier, the same, or harder?',
  },
  'atrial-fibrillation': {
    id: 'atrial-fibrillation',
    label: 'Atrial fibrillation',
    focus: 'Heart rhythm monitoring',
    metrics: ['heart-rate', 'hrv', 'sleep-hours'],
    symptoms: ['irregular-heartbeat', 'heart-racing', 'dizziness', 'shortness-of-breath', 'fatigue', 'chest-tightness'],
    quickActions: [
      { id: 'log-pulse', label: 'Take pulse' },
      { id: 'log-bp', label: 'Log BP' },
      { id: 'symptoms', label: 'Symptoms' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [{ kind: 'pulse', daysPerWeek: 5 }, GOAL_BP, GOAL_ADHERENCE],
    earning: ['pulse-log', 'log-bp', 'all-meds', 'coach-checkin'],
    coachFocus:
      'Atrial fibrillation. Rate and rhythm are the story: a resting pulse that is racing or plainly irregular, especially with dizziness or breathlessness, is worth a call. The anticoagulant is the highest-stakes medication in their plan; never advise stopping or changing it, and treat any bleeding, bruising or missed-dose question as one for the clinic.',
    checkInPrompt: 'Take your pulse at rest for thirty seconds and log it. Tick the box if it felt irregular.',
  },
  cad: {
    id: 'cad',
    label: 'Coronary artery disease',
    focus: 'Risk factor control',
    metrics: ['heart-rate', 'exertion-minutes', 'weight-lbs'],
    symptoms: ['chest-tightness', 'shortness-of-breath', 'fatigue', 'dizziness', 'heart-racing'],
    quickActions: [
      { id: 'log-bp', label: 'Log BP' },
      { id: 'log-steps', label: 'Log steps' },
      { id: 'symptoms', label: 'Symptoms' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [GOAL_BP, { kind: 'steps', target: 7000 }, GOAL_ADHERENCE],
    earning: ['log-bp', 'all-meds', 'coach-checkin', 'steps-goal'],
    coachFocus:
      'Coronary artery disease. The work is risk factors: blood pressure, the statin taken every day, movement, and not smoking. Chest discomfort brought on by exertion and relieved by rest is the pattern to ask about and to escalate when it changes.',
    checkInPrompt: 'Log a blood pressure reading, and tell me how much walking you managed yesterday.',
  },
  hypertension: {
    id: 'hypertension',
    label: 'Hypertension',
    focus: 'Blood pressure management',
    metrics: ['heart-rate', 'weight-lbs', 'sleep-hours'],
    symptoms: ['headache', 'dizziness', 'chest-tightness', 'shortness-of-breath', 'heart-racing'],
    quickActions: [
      { id: 'log-bp', label: 'Log BP' },
      { id: 'log-steps', label: 'Log steps' },
      { id: 'symptoms', label: 'Symptoms' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [GOAL_BP, { kind: 'steps', target: 8000 }, GOAL_ADHERENCE],
    earning: ['log-bp', 'all-meds', 'coach-checkin', 'steps-goal', 'device-day'],
    coachFocus:
      'Hypertension. The weekly average matters more than any single reading, and technique changes the number as much as the medication does: sitting five minutes first, feet flat, arm at heart level. Salt is the lever they control day to day. Readings that fall well below target matter too, because the dose may be doing more than intended.',
    checkInPrompt: 'Take a reading when you have been sitting quietly for five minutes, and log it.',
  },
  other: {
    id: 'other',
    label: 'Cardiac care',
    focus: 'Staying on your plan',
    metrics: ['heart-rate', 'weight-lbs', 'sleep-hours', 'hrv'],
    symptoms: ['chest-tightness', 'shortness-of-breath', 'heart-racing', 'dizziness', 'swelling', 'headache', 'fatigue'],
    quickActions: [
      { id: 'log-bp', label: 'Log BP' },
      { id: 'log-steps', label: 'Log steps' },
      { id: 'symptoms', label: 'Symptoms' },
      { id: 'coach', label: 'Ask coach' },
    ],
    defaultGoals: [GOAL_BP, { kind: 'steps', target: 8000 }, GOAL_ADHERENCE],
    earning: ['log-bp', 'all-meds', 'coach-checkin', 'steps-goal', 'device-day'],
    coachFocus: 'General cardiac care: readings, medications taken as prescribed, and movement. Escalate chest discomfort, breathlessness and fainting.',
    checkInPrompt: 'Log a reading and tick off your doses. It takes under a minute.',
  },
};

/**
 * Which track a patient is on when they carry more than one diagnosis. Total and fixed: heart
 * failure outranks everything because its daily job is the most time-critical, and a patient with
 * no diagnoses recorded gets the general track, which is what every patient had before tracks
 * existed.
 */
export const TRACK_PRIORITY: readonly DiagnosisCode[] = ['heart-failure', 'post-mi', 'atrial-fibrillation', 'cad', 'hypertension', 'other'];

export function trackFor(diagnoses: readonly Diagnosis[]): CareTrack {
  for (const code of TRACK_PRIORITY) {
    if (diagnoses.some((d) => d.code === code)) return TRACKS[code];
  }
  return TRACKS.other;
}

/** How each metric is named and rendered. One definition, so the tile and the coach agree. */
export const METRIC_META: Readonly<Record<MetricKind, { label: string; unit: string; decimals: number }>> = {
  'heart-rate': { label: 'Resting pulse', unit: 'bpm', decimals: 0 },
  hrv: { label: 'HRV', unit: 'ms', decimals: 0 },
  'sleep-hours': { label: 'Sleep', unit: 'hrs', decimals: 1 },
  'weight-lbs': { label: 'Weight', unit: 'lbs', decimals: 1 },
  'exertion-minutes': { label: 'Active minutes', unit: 'min', decimals: 0 },
};
